<template>
  <main class="life-atlas">
    <section
      v-if="featured"
      class="atlas-feature atlas-shell"
      aria-labelledby="atlas-feature-title"
    >
      <div class="atlas-feature-heading">
        <h1 id="atlas-feature-title">
          Featured <em>Biographies</em>
        </h1><BiographyStar class="atlas-outline-star" />
      </div>
      <router-link
        :to="profileLink(featured)"
        class="atlas-feature-story"
      >
        <div class="atlas-feature-image">
          <BiographyCover
            :profile="featured"
            prefer-avatar
            loading="eager"
          />
        </div>
        <div
          class="atlas-feature-shade"
          aria-hidden="true"
        ></div>
        <div
          class="atlas-feature-index"
          aria-hidden="true"
        >
          01
        </div>
        <div class="atlas-feature-content">
          <span class="atlas-feature-tag">THE FEATURED LIFE</span>
          <h3>Pepe<br><em>Moreno.</em></h3>
          <p>{{ featuredLine }}</p>
          <span class="atlas-button atlas-button-paper">Enter his world <span aria-hidden="true">↗</span></span>
        </div>
        <span class="atlas-feature-credit">ART. MEMORY. A LIFE IN CREATION.</span>
      </router-link>
    </section>

    <section
      class="atlas-discovery atlas-shell"
      aria-labelledby="explore-heading"
      :aria-busy="String(loading)"
    >
      <h2
        id="explore-heading"
        class="atlas-sr-only"
      >
        Explore biographies
      </h2>
      <form
        class="atlas-search"
        role="search"
        @submit.prevent="searchProfiles"
      >
        <label class="atlas-search-field"><v-icon size="22">{{ icons.mdiMagnify }}</v-icon><span class="atlas-sr-only">Search biographies</span><input
          v-model="search"
          type="search"
          placeholder="Find a name. Discover a story."
          maxlength="100"
        ></label>
        <button
          type="submit"
          :disabled="loading"
          class="atlas-search-submit"
        >
          Search <span aria-hidden="true">↗</span>
        </button>
        <label class="atlas-sort"><span class="atlas-sr-only">Sort biographies</span><select
          v-model="sort"
          @change="searchProfiles"
        ><option value="newest">Newest profiles</option><option value="name">Name A to Z</option></select></label>
      </form>
      <div
        v-if="error"
        class="atlas-state"
        role="alert"
      >
        <h3>A little pause in the story.</h3><p>{{ error }}</p><button
          class="atlas-button atlas-button-dark"
          @click="loadProfiles"
        >
          Try again
        </button>
      </div>
      <div
        v-else-if="loading"
        class="atlas-loading"
        role="status"
      >
        <BiographyStar class="atlas-spark" /> Gathering the stories…
      </div>
      <template v-else-if="users.length">
        <div class="atlas-result-note">
          <span>{{ appliedSearch ? `RESULTS FOR “${appliedSearch}”` : 'PUBLIC PROFILES' }}</span><span>PAGE {{ String(page).padStart(2, '0') }}</span>
        </div>
        <div class="atlas-gallery">
          <component
            :is="isAvailable(user) ? 'router-link' : 'article'"
            v-for="(user, index) in users"
            :key="user.id"
            :to="isAvailable(user) ? profileLink(user) : undefined"
            class="atlas-person"
            :class="{ 'atlas-person-symbolic': isSymbolic(user), 'atlas-person-coming-soon': !isAvailable(user) }"
            :style="{ '--poster-color': posterColor(user) }"
          >
            <div class="atlas-person-image">
              <BiographyCover
                :profile="user"
                @source-change="setCoverState(user, $event)"
              />
            </div>
            <div
              v-if="isSymbolic(user)"
              class="atlas-person-initials"
              aria-hidden="true"
            >
              {{ initials(user) }}
            </div>
            <div
              class="atlas-person-gradient"
              aria-hidden="true"
            ></div>
            <span class="atlas-person-number">{{ String((page - 1) * 12 + index + 1).padStart(2, '0') }}</span>
            <span
              v-if="isAvailable(user)"
              class="atlas-person-open"
              aria-hidden="true"
            >↗</span>
            <span
              v-else
              class="atlas-person-status"
            >Coming soon</span>
            <div class="atlas-person-copy">
              <span class="atlas-person-eyebrow">A LIFE IN WORDS & PICTURES</span><h3>{{ user.name || user.username }}</h3><span class="atlas-person-handle">@{{ user.username }}</span><p v-if="user.bio">
                {{ excerpt(user.bio) }}
              </p>
            </div>
          </component>
        </div>
        <nav
          v-if="page > 1 || hasMore"
          class="atlas-pagination"
          aria-label="Biography pages"
        >
          <button
            :disabled="page === 1"
            @click="changePage(-1)"
          >
            <span aria-hidden="true">←</span> Previous
          </button><span aria-live="polite">{{ String(page).padStart(2, '0') }}</span><button
            :disabled="!hasMore"
            @click="changePage(1)"
          >
            More lives to discover <span aria-hidden="true">→</span>
          </button>
        </nav>
      </template>
      <div
        v-else
        class="atlas-state"
        role="status"
      >
        <h3>{{ appliedSearch ? 'A story still to be found.' : 'The next chapter is on its way.' }}</h3><p>{{ appliedSearch ? 'Try a different name or username.' : 'Public biographies will appear here as people share their stories.' }}</p><button
          v-if="appliedSearch"
          class="atlas-button atlas-button-dark"
          @click="clearSearch"
        >
          Explore all biographies
        </button>
      </div>
    </section>
  </main>
</template>

<script>
import Biographies from './Biographies.vue'
import BiographyCover from '@/components/BiographyCover.vue'
import BiographyStar from '@/components/BiographyStar.vue'

export default {
  name: 'BiographiesVisual',
  components: { BiographyCover, BiographyStar },
  extends: Biographies,
  data: () => ({ fallbackCovers: {} }),
  computed: {
    featuredLine() {
      const text = this.excerpt(this.featured && this.featured.bio)

      return text ? (text.match(/^[\s\S]*?[.!?](?:\s|$)/) || [text])[0].trim() : 'Discover the memories, ideas, and experiences behind a creative life.'
    },
  },
  methods: {
    isAvailable(user) {
      return user.username === 'pepe-moreno'
    },
    isSymbolic(user) {
      return (!user.cover_url && !user.avatar) || this.fallbackCovers[user.id]
    },
    setCoverState(user, state) {
      this.$set(this.fallbackCovers, user.id, state.fallback)
    },
    initials(user) {
      return (user.name || user.username).trim().split(/\s+/).slice(0, 2).map(word => word[0])
        .join('')
        .toUpperCase()
    },
    posterColor(user) {
      const colors = ['#66557b', '#42645b', '#8a5646', '#405b78', '#756047', '#605780']

      return colors[Number(user.id) % colors.length]
    },
  },
}
</script>

<style scoped>
.life-atlas { --paper: #f4f1e9; --ink: #252925; --muted: #64685e; --line: #d4d4c9; --accent: #7757b7; background: var(--paper); color: var(--ink); overflow: hidden; font-family: Inter, Arial, sans-serif; }
.atlas-shell { width: min(1320px, calc(100% - 96px)); margin: 0 auto; }
.atlas-spark { font-size: 32px; line-height: 1; color: var(--accent); }
.life-atlas em { font-family: Georgia, 'Times New Roman', serif; font-weight: 400; }
.atlas-button { display: inline-flex; justify-content: space-between; gap: 32px; align-items: center; padding: 17px 22px; font-size: 12px; font-weight: 600; border-radius: 2px; text-decoration: none; transition: transform .2s; }
.atlas-button span { font-size: 24px; line-height: 1; }
.atlas-button-dark { background: var(--ink); color: var(--paper) !important; }
.atlas-button-paper { background: #f4f1e9; color: #252925 !important; }
.atlas-button:hover { transform: translateY(-3px); }
.atlas-feature { padding-top: 24px; }
.atlas-feature-heading { display: flex; align-items: center; justify-content: space-between; padding: 28px 0 32px; }
.atlas-feature-heading h1 { font-size: clamp(27px, 3.3vw, 46px); font-weight: 400; letter-spacing: -.04em; }
.atlas-outline-star { font-size: 46px; color: var(--accent); }
.atlas-feature-story { display: block; position: relative; min-height: 640px; background: #1d2924; color: #fff !important; text-decoration: none; overflow: hidden; filter: sepia(.9) saturate(.7) contrast(.95); }
.atlas-feature-image { position: absolute; inset: 0 0 0 27%; }
.atlas-feature-shade { position: absolute; inset: 0; background: linear-gradient(90deg, #172620 0%, #172620e8 24%, #17262060 60%, #17262000 84%), linear-gradient(0deg, #101f18b3, transparent 40%); }
.atlas-feature-index { position: absolute; top: 28px; right: 32px; font-family: Georgia, serif; font-size: 80px; line-height: 1; color: #ffffffb3; }
.atlas-feature-content { position: relative; max-width: 490px; padding: 70px 52px; }
.atlas-feature-tag { font-size: 9px; letter-spacing: .23em; border: 1px solid #ffffff66; padding: 8px 12px; }
.atlas-feature-content h3 { font-family: Georgia, serif; font-size: clamp(65px, 7vw, 105px); font-weight: 400; line-height: .95; letter-spacing: -.055em; margin: 40px 0 24px; color: #fff; }
.atlas-feature-content h3 em { color: #d5dfa7; }
.atlas-feature-content p { max-width: 310px; font-size: 14px; line-height: 1.8; color: #e2e5dc; margin-bottom: 30px; }
.atlas-feature-credit { position: absolute; right: 32px; bottom: 30px; font-size: 9px; letter-spacing: .17em; color: #fff; }
.atlas-discovery { padding-top: 48px; padding-bottom: 80px; scroll-margin-top: 80px; }
.atlas-search { display: flex; align-items: center; border-top: 1px solid var(--line); border-bottom: 1px solid var(--line); margin-bottom: 28px; padding: 12px 0; gap: 18px; }
.atlas-search-field { display: flex; align-items: center; flex: 1; gap: 14px; min-width: 0; }
.atlas-search-field .v-icon { color: var(--muted); }
.atlas-search input { width: 100%; min-width: 0; padding: 10px 0; font-size: 14px; color: var(--ink); }
.atlas-search input::placeholder { color: var(--muted); opacity: 1; }
.atlas-search-submit { display: flex; align-items: center; gap: 18px; font-size: 12px; padding: 10px; color: var(--ink); }
.atlas-search-submit span { font-size: 22px; }
.atlas-sort { border-left: 1px solid var(--line); padding-left: 24px; }
.atlas-sort select { font-size: 12px; color: var(--ink); padding: 10px 20px 10px 4px; appearance: auto; }
.atlas-sort option { background: var(--paper); }
.atlas-result-note { display: flex; justify-content: space-between; gap: 12px; margin: 0 0 24px; font-size: 9px; letter-spacing: .12em; color: var(--muted); overflow-wrap: anywhere; }
.atlas-gallery { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 22px; align-items: start; }
.atlas-person { position: relative; display: block; height: 490px; overflow: hidden; background: #343c37; color: #fff !important; text-decoration: none; }
.atlas-person-image { position: absolute; inset: 0; transition: transform .7s ease; }
a.atlas-person:hover .atlas-person-image { transform: scale(1.04); }
.atlas-person-symbolic { background: var(--poster-color); }
.atlas-person-symbolic .atlas-person-image { mix-blend-mode: luminosity; opacity: .36; }
.atlas-person-initials { position: absolute; inset: 10% 0 auto; text-align: center; font-family: Georgia, serif; font-size: clamp(80px, 9vw, 140px); line-height: 1.2; letter-spacing: -.09em; color: #ffffffbd; }
.atlas-person-gradient { position: absolute; inset: 0; background: linear-gradient(0deg, #10201aee, #14251c88 30%, #10201a00 73%); }
.atlas-person-number { position: absolute; left: 24px; top: 22px; font-size: 10px; letter-spacing: .16em; text-shadow: 0 1px 5px #000; }
.atlas-person-open { position: absolute; top: 16px; right: 16px; display: grid; place-items: center; width: 38px; height: 38px; border-radius: 50%; background: #f4f1e9; color: #252925; font-size: 24px; }
.atlas-person-coming-soon { cursor: default; }
.atlas-person-status { position: absolute; top: 20px; right: 20px; padding: 8px 12px; border-radius: 20px; background: #f4f1e9; color: #252925; font-size: 11px; font-weight: 600; letter-spacing: .03em; }
.atlas-person-copy { position: absolute; inset: auto 26px 30px; }
.atlas-person-eyebrow { font-size: 8px; font-weight: 600; letter-spacing: .16em; color: #d4ddce; }
.atlas-person-copy h3 { font-family: Georgia, serif; font-size: clamp(25px, 2.5vw, 37px); line-height: 1.12; font-weight: 400; letter-spacing: -.025em; margin: 12px 0 10px; color: #fff; overflow-wrap: anywhere; }
.atlas-person-handle { font-size: 11px; color: #e3e6dc; overflow-wrap: anywhere; }
.atlas-person-copy p { font-size: 12px; line-height: 1.6; margin: 14px 0 0; color: #edf0e9; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; }
.atlas-pagination { display: flex; justify-content: space-between; gap: 18px; align-items: center; padding: 30px 0 0; margin-top: 28px; border-top: 1px solid var(--line); }
.atlas-pagination button { display: flex; align-items: center; gap: 12px; font-size: 12px; color: var(--ink); padding: 10px 0; }
.atlas-pagination > span { font-family: Georgia, serif; font-size: 28px; }
.atlas-pagination button:disabled, .atlas-search-submit:disabled { opacity: .4; cursor: default; }
.atlas-loading { min-height: 400px; display: flex; gap: 16px; align-items: center; justify-content: center; font-family: Georgia, serif; font-size: 24px; }
.atlas-state { padding: 70px 0; }
.atlas-state h3 { font-family: Georgia, serif; font-size: 40px; font-weight: 400; margin-bottom: 16px; }
.atlas-state p { margin-bottom: 24px; }
.atlas-sr-only { position: absolute; width: 1px; height: 1px; overflow: hidden; clip-path: inset(50%); white-space: nowrap; }
.life-atlas a:focus-visible, .life-atlas button:focus-visible, .life-atlas input:focus-visible, .life-atlas select:focus-visible { outline: 3px solid #a581e2; outline-offset: 5px; }
.theme--dark .life-atlas { --paper: #202722; --ink: #eeeee3; --muted: #b5bdb0; --line: #444d44; --accent: #c6abe9; }
@media (max-width: 1100px) {
  .atlas-shell { width: calc(100% - 56px); }
  .atlas-feature-content { padding-left: 35px; }
  .atlas-person { height: 420px; }
  .atlas-person-copy { left: 20px; right: 20px; bottom: 24px; }
}
@media (max-width: 760px) {
  .atlas-shell { width: calc(100% - 40px); }
  .atlas-feature { padding-top: 38px; }
  .atlas-feature-heading { padding: 24px 0; }
  .atlas-feature-heading h1 { font-size: 27px; }
  .atlas-outline-star { font-size: 32px; }
  .atlas-feature-story { min-height: 660px; }
  .atlas-feature-image { inset: 0; }
  .atlas-feature-shade { background: linear-gradient(0deg, #172620 0%, #172620e8 23%, #17262055 63%, #17262000 100%); }
  .atlas-feature-content { position: absolute; bottom: 0; padding: 32px 24px 54px; }
  .atlas-feature-content h3 { font-size: 75px; margin: 24px 0 18px; }
  .atlas-feature-content p { font-size: 13px; margin-bottom: 24px; }
  .atlas-feature-index { font-size: 54px; right: 20px; top: 20px; }
  .atlas-feature-credit { right: auto; left: 24px; bottom: 22px; font-size: 7px; }
  .atlas-discovery { padding-top: 32px; padding-bottom: 50px; }
  .atlas-search { flex-wrap: wrap; gap: 8px; }
  .atlas-search-field { flex-basis: calc(100% - 88px); }
  .atlas-search input { font-size: 16px; }
  .atlas-search-submit { padding-right: 0; }
  .atlas-sort { width: 100%; border-left: 0; border-top: 1px solid var(--line); padding: 8px 0 0; }
  .atlas-sort select { width: 100%; font-size: 16px; }
  .atlas-gallery { grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 16px; }
  .atlas-person { height: 380px; margin-top: 0; }
  .atlas-person-copy h3 { font-size: 27px; }
  .atlas-person-initials { font-size: 90px; }
  .atlas-pagination { gap: 10px; }
  .atlas-pagination button { font-size: 10px; gap: 7px; }
}
@media (max-width: 480px) {
  .atlas-gallery { grid-template-columns: 1fr; gap: 20px; }
  .atlas-person { height: 440px; }
  .atlas-person-copy { left: 26px; right: 26px; bottom: 30px; }
  .atlas-person-copy h3 { font-size: 35px; }
  .atlas-person-initials { font-size: 130px; }
  .atlas-feature-heading h1 { font-size: 24px; }
  .atlas-feature-content h3 { font-size: 70px; }
  .atlas-result-note { font-size: 8px; }
}
@media (prefers-reduced-motion: reduce) { .atlas-person-image, .atlas-button { transition: none; } a.atlas-person:hover .atlas-person-image, .atlas-button:hover { transform: none; } }
</style>
